import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Select } from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import { useCreateDraftClaim } from "../../hooks/claims.mutations";
import { ICreateDraft } from "../../types/claims.types";
import { useFetchInsuranceProviders } from "../../../clients/hooks/clients.queries";
import {
    removeEnumUnderscore,
    handleRemoveEmptyParamFromQueryParams,
} from "../../../../utils/helpers";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useFetchSessionNote } from "../../../../hooks/queries/note";

type Options = {
    label: string;
    value: string;
};

const schema = yup.object().shape({
    insurance_provider_id: yup
        .string()
        .required("Insurance provider is required"),
});

interface CreateClaimWithInsuranceProps {
    onFormSubmit: () => void;
}

export default function CreateClaimWithInsurance({
    onFormSubmit,
}: CreateClaimWithInsuranceProps) {
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const queryClient = useQueryClient();
    // Toast for success and error states
    const { toast } = useToast();

    const insuranceProviders = useFetchInsuranceProviders({
        client_id: clientId,
    });

    const { data: noteDetails } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    // hook for updating claim status
    const createADraftClaim = useCreateDraftClaim();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ICreateDraft>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const insuranceProvidersSelectOptions = (
        insuranceProviders.data?.data || []
    )
        .sort((a, b) => a.alias.localeCompare(b.alias))
        .map((provider) => ({
            label: `${removeEnumUnderscore(provider.alias).toUpperCase()}  ${
                provider.insurance_member_number
                    ? `(ID: ${provider.insurance_member_number})`
                    : ""
            }`,
            value: provider.insurance_provider_id,
        }));

    const onSubmit = (data: ICreateDraft) => {
        const payload = {
            note_id: noteId,
            insurance_provider_id: data.insurance_provider_id,
        };
        createADraftClaim.mutate(payload, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ["get-claim", { note_id: noteId }],
                });
                await queryClient.invalidateQueries({
                    queryKey: [
                        "invoices",
                        handleRemoveEmptyParamFromQueryParams({
                            invoice_id: noteDetails?.data?.invoice_id,
                        }),
                    ],
                });
                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                toast({
                    mode: "success",
                    message: "Claim created successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not create claim at this time",
                });
            },
        });
    };

    if (insuranceProviders.isLoading) {
        return <Loader wrapperClassName="h-[150px]" />;
    }

    return (
        <form
            id="create-claim-with-insurance"
            onSubmit={handleSubmit(onSubmit)}
        >
            <Alert
                type="info"
                description="This client is a self-pay client. A claim will be created with the selected insurance provider."
                classNames="mb-[--spacing-form-items]"
            />
            <Controller
                name="insurance_provider_id"
                control={control}
                render={({ field }) => (
                    <Select
                        label="Insurance provider"
                        placeholder="Select insurance provider"
                        isSearchable
                        isLongListInDialog
                        onChange={(val) => {
                            field.onChange((val as Options).value);
                        }}
                        hasError={!!errors.insurance_provider_id}
                        errorText={errors.insurance_provider_id?.message}
                        options={insuranceProvidersSelectOptions}
                    />
                )}
            />
        </form>
    );
}
